<template>
  <!-- Tabbed privacy consent -->
  <div class="boxed-category">
    <md-subheader>
      Credit Report&nbsp;
      <!-- The below button will only be shown to Current Credit Report -->
      <div>
        <loading-icon-text-button
          class="md-warning"
          v-if="
            veda_report &&
            veda_report.success &&
            userHasPermission('re-run veda') &&
            !veda_report.is_credit_file_outdated &&
            (version == 'Current' || version == 'Alternative')
          "
          @click="removeCreditReport()"
          :loading="loading"
          icon="delete"
          :disabled="disabledUpdate"
        >
          REMOVE INCORRECT FILE
        </loading-icon-text-button>
        <loading-icon-text-button
          class="md-primary"
          v-if="
            veda_report &&
            veda_report.success &&
            userHasPermission('re-run veda') &&
            !veda_report.is_credit_file_outdated &&
            (version == 'Current' || version == 'Alternative')
          "
          @click="showRefreshDialog()"
          :loading="loading"
          icon="refresh"
          :disabled="disabledUpdate"
        >
          Update Credit Report
        </loading-icon-text-button>
        <span v-if="disabledUpdate">{{ tooltipMessage }}</span>
      </div>
    </md-subheader>

    <edit-applicant-veda-report
      v-model="applicant"
      :index="index"
      @application-change="applicationChange"
      ref="editApplicantVedaReport"
      :version="version"
    />

    <md-dialog class="md-layout" :md-active.sync="removeDialog">
      <div class="wrapper">
        <div>
          <h4>Why do you wish to remove the current credit file?</h4>
        </div>

        <div md-card class="declined_dialog" style="display: flex">
          <label>Select a reason</label>
          <md-radio v-model="selectedReason" value="File returned by Equifax does not belong to applicant">
            File returned by Equifax does not belong to applicant
          </md-radio>
          <md-radio v-model="selectedReason" value="Other"> Other </md-radio>
          <v-textarea v-if="selectedReason == 'Other'" label="Please provide more details" v-model="typedReasons" outlined></v-textarea>
        </div>
        <md-dialog-actions>
          <md-button class="md-primary md-simple" @click="onCloseDialog"> Cancel </md-button>
          <loading-icon-text-button @click="confirmRemove" icon="save" :loading="loading"> Confirm </loading-icon-text-button>
        </md-dialog-actions>
      </div>
    </md-dialog>
    <md-dialog-alert :md-active.sync="error_dialog.show" :md-title="error_dialog.title" :md-content="error_dialog.content" />
    <md-dialog class="md-layout" :md-active.sync="refreshDialog">
      <div class="wrapper">
        <div>
          <h4>Which credit report option do you need?</h4>
        </div>
        <div md-card class="declined_dialog" style="display: flex">
          <md-radio v-model="selectedRefreshCreditType" value="Comprehensive"> Comprehensive Score <b>ONLY</b> </md-radio>
          <md-radio v-model="selectedRefreshCreditType" value="Comprehensive & One Score"> Comprehensive with <b>ONE SCORE</b> </md-radio>
        </div>
        <md-dialog-actions>
          <md-button class="md-primary md-simple" @click="onCloseRefeshDialog"> Cancel </md-button>
          <loading-icon-text-button @click="confirmRefresh" icon="check" :loading="loading"> OK </loading-icon-text-button>
        </md-dialog-actions>
      </div>
    </md-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ValidatedInputs from '@/components/ValidatedInput'
import EditApplicantVedaReport from './EditApplicantVedaReport'
import EditApplicantPrivacyConsent from './EditApplicantPrivacyConsent'
import LoadingIconTextButton from '@/components/LoadingIconTextButton'
import ConfirmDialog from '../Dialogs/ConfirmDialog'

export default {
  components: {
    EditApplicantVedaReport,
    EditApplicantPrivacyConsent,
    LoadingIconTextButton,
    ...ValidatedInputs,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    index: {
      required: true,
      default: 0,
    },
    version: {
      required: false,
      default: 'Current',
    },
  },
  data() {
    return {
      loading: false,
      disabledUpdate: false,
      tooltipMessage: '',
      removeDialog: false,
      typedReasons: '',
      error_dialog: {
        show: false,
        title: '',
        content: '',
      },
      selectedReason: null,
      showTextArea: false,
      refreshDialog: false,
      selectedRefreshCreditType: null,
    }
  },
  computed: {
    ...mapGetters({
      application: 'application',
      userHasPermission: 'userHasPermission',
      userHasRole: 'userHasRole',
    }),

    // internal reference to the v-model on this component, handles incoming and outgoing changes
    applicant: {
      get() {
        return this.value
      },
      set(applicant) {
        this.$emit('input', applicant)
      },
    },

    veda_report: {
      get() {
        if (this.version === 'Current') {
          return this.applicant.veda_report
        } else if (this.version === 'Previous') {
          return this.applicant.previous_veda_report
        } else if (this.version === 'Alternative') {
          return this.applicant.alternative_veda_report
        }
        return {}
      },
    },
  },
  methods: {
    showRefreshDialog() {
      this.refreshDialog = true
    },
    onCloseRefeshDialog() {
      this.refreshDialog = false
      this.selectedRefreshCreditType = null
    },
    async confirmRefresh() {
      if (this.selectedRefreshCreditType === null) {
        this.showNotification('', 'Please select a credit report type', 'OK')
        return
      }

      this.refreshDialog = false
      try {
        this.loading = true
        let alternative = this.version == 'Alternative'
        if (this.selectedRefreshCreditType === 'Comprehensive & One Score') {
          await this.$refs.editApplicantVedaReport.fetchComprehensiveAndOneScoreReport(true, null, alternative)
        } else {
          await this.$refs.editApplicantVedaReport.getVeda(true, null, alternative)
        }
      } catch (error) {
        console.error('Error removing credit report:', error)
      } finally {
        this.loading = false
        this.selectedRefreshCreditType = null
      }
    },

    applicationChange() {
      this.$emit('application-change')
      this.loading = false
    },

    updateCreditReportRefreshStatus() {
      if (this.veda_report) {
        this.disabledUpdate =
          !this.veda_report.is_allow_refresh_credit_file && !(this.userHasRole('super-admin') || this.userHasRole('Broker'))
        if (this.disabledUpdate) {
          this.tooltipMessage = 'Credit report already requested in the last 7 days'
        }
      }
    },
    async removeCreditReport() {
      const res = await this.showNotification(
        'Confirm',
        'Are you sure you wish to remove current credit file? This cannot be undone and a new search will need to be completed.',
        'OK',
        'Cancel',
      )
      if (!res) {
        return
      }

      this.removeDialog = true
    },
    onCloseDialog() {
      this.removeDialog = false
    },
    async confirmRemove() {
      if (this.selectedReason === 'Other' && (!this.typedReasons || this.typedReasons.trim().length < 20)) {
        this.showDialogPopup('Reason requires at least 20 characters', 'Please enter some text')
        return
      }

      const auditMessage = this.selectedReason === 'Other' ? this.typedReasons.trim() : this.selectedReason

      try {
        this.loading = true
        await this.$refs.editApplicantVedaReport.removeCreditReport(this.applicant.id, auditMessage)
      } catch (error) {
        console.error('Error removing credit report:', error)
      } finally {
        this.loading = false
        this.typedReasons = ''
        this.removeDialog = false
      }
    },
    showDialogPopup(title, message) {
      this.error_dialog.title = title
      this.error_dialog.content = message
      this.error_dialog.show = true
    },
    showNotification(title, message, confirmText, cancelText = null) {
      return this.$modal.show(ConfirmDialog, {
        title,
        message,
        confirmText,
        cancelText
      })
    },
  },
  mounted() {
    this.updateCreditReportRefreshStatus();
  },
  watch: {
    veda_report: {
      handler() {
        this.updateCreditReportRefreshStatus();
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.md-subheader {
  display: flex;
  justify-content: space-between;
}
.wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 40vw;
  height: auto;
  margin-bottom: 20px;
  padding: 30px;

  .md-table {
    height: 20vh;
  }

  .md-dialog-actions {
    align-self: end;
    padding-right: 30px;
  }

  .declined_dialog {
    align-self: start;
    width: 100%;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
  }

  .md-radio-label {
    color: black;
  }
}

</style>
